import { useEffect, useState } from "react";
import { API_BASE, DEFAULT_HEADERS } from "../../services/apiClient";
import { getCurrentUserId } from "../../services/authService";
import { followeeUserId } from "../../services/connectionsService";

export const useConnections = () => {
  const [connectionsCount, setConnectionsCount] = useState(0);
  const [pendingConnectionsCount, setPendingConnectionsCount] = useState(0);
  const [connectedUserIds, setConnectedUserIds] = useState([]);
  const [connectedEmails, setConnectedEmails] = useState([]);

  useEffect(() => {
    const loadConnectionsCountAndIds = async () => {
      try {
        const uid = getCurrentUserId();
        if (!uid) {
          setConnectionsCount(0);
          setConnectedUserIds([]);
          setConnectedEmails([]);
          return;
        }
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");

        const baseHeaders = {
          "Content-Type": "application/json",
          ...DEFAULT_HEADERS,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-User-Id": String(uid),
        };

        const followeesUrl = `${API_BASE}/follow/followees?user_id=${encodeURIComponent(
          uid
        )}&limit=200&offset=0`;
        const pendingUrl = `${API_BASE}/follow/requests/incoming?status=pending&limit=200`;

        const fetchList = async (url) => {
          const res = await fetch(url, { headers: baseHeaders });
          if (!res.ok) throw new Error(`Failed to load connections (${res.status})`);
          try {
            return await res.json();
          } catch {
            return null;
          }
        };

        const data = await fetchList(followeesUrl);
        const pendingData = await fetchList(pendingUrl).catch(() => null);

        let inferred = 0;
        let userIds = [];
        let emails = [];
        if (data) {
          const t = Number.isFinite(data.total) ? data.total : null;
          const c = Number.isFinite(data.count) ? data.count : null;
          const rlen = Array.isArray(data.results) ? data.results.length : null;
          inferred = t ?? c ?? rlen ?? 0;
          if (Array.isArray(data.results)) {
            userIds = data.results
              .map((r) => followeeUserId(r))
              .filter((id) => id != null);

            const grabEmail = (r) =>
              (
                r?.email ||
                r?.user?.email ||
                r?.profile?.email ||
                r?.contact_email ||
                r?.primary_email ||
                r?.followee_email ||
                ""
              )
                .toString()
                .trim()
                .toLowerCase();

            emails = data.results.map((r) => grabEmail(r)).filter((e) => e);
          }
        }

        const pendingTotal = (() => {
          if (!pendingData) return 0;
          // New /follow/requests/incoming shape: { items, count }
          if (Array.isArray(pendingData.items)) return pendingData.items.length;
          if (Number.isFinite(pendingData.count)) return pendingData.count;
          if (Number.isFinite(pendingData.total)) return pendingData.total;
          if (Array.isArray(pendingData.results)) return pendingData.results.length;
          if (Array.isArray(pendingData)) return pendingData.length;
          return 0;
        })();

        setConnectionsCount(Number.isFinite(inferred) ? inferred : 0);
        setPendingConnectionsCount(Number.isFinite(pendingTotal) ? pendingTotal : 0);
        setConnectedUserIds(Array.isArray(userIds) ? userIds : []);
        setConnectedEmails(
          Array.isArray(emails) ? Array.from(new Set(emails)) : []
        );
      } catch (e) {
        console.error("Failed to fetch connections count or IDs:", e);
        setConnectionsCount(0);
        setPendingConnectionsCount(0);
        setConnectedUserIds([]);
        setConnectedEmails([]);
      }
    };
    loadConnectionsCountAndIds();
  }, []);

  return {
    connectionsCount,
    pendingConnectionsCount,
    connectedUserIds,
    connectedEmails,
  };
};
